import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPost } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { Card, PageHeader, KpiRow, Badge } from "../components/dc";

const gbp = (v) => "£" + Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });
const tierLabel = { t4: "Code variant", t5: "Description", manual: "Manual" };
const tierTone = { t4: "amber", t5: "cyan", manual: "muted" };

/* One candidate card: shows the invoice line, a ranked shortlist of file SKUs to
   choose from, and approve / reject. Approving saves a reusable alias. */
function CandidateCard({ c, onApprove, onReject, busy }) {
  const shortlist = (c.shortlist || []).filter((s) => s && s.sku);
  const [chosen, setChosen] = useState(c.proposed_sku || (shortlist[0] && shortlist[0].sku) || "");
  const [manualQ, setManualQ] = useState("");
  const [manualResults, setManualResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Manual match: search the supplier's price-file SKUs (by code or description)
  // and pick one. Debounced lightly so typing doesn't hammer the backend.
  useEffect(() => {
    const q = manualQ.trim();
    if (q.length < 2) { setManualResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      apiGet(`/match-review/price-file-skus?supplier=${encodeURIComponent(c.supplier)}&q=${encodeURIComponent(q)}&limit=25`)
        .then((d) => { if (!cancelled) setManualResults(d?.items || []); })
        .catch(() => { if (!cancelled) setManualResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [manualQ, c.supplier]);

  const manualPicked = chosen && !shortlist.some((s) => s.sku === chosen);

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
        <div>
          <span style={{ fontWeight: 700, color: "var(--color-text-1)" }}>{c.supplier}</span>
          <span style={{ marginLeft: 10 }}><Badge tone={tierTone[c.tier] || "cyan"}>{tierLabel[c.tier] || c.tier}</Badge></span>
        </div>
        <div style={{ fontSize: 13, color: "var(--color-text-3)" }}>
          {c.line_count} line{c.line_count === 1 ? "" : "s"} · <b style={{ color: "var(--color-text-1)" }}>{gbp(c.total_value)}</b>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 14 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-4)", marginBottom: 4 }}>Invoice line</div>
          <div style={{ fontFamily: "var(--font-mono, monospace)", fontWeight: 600, color: "var(--color-text-1)" }}>{c.invoice_code || "(no code)"}</div>
          <div style={{ fontSize: 13, color: "var(--color-text-3)", marginTop: 4 }}>{c.description || "—"}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-4)", marginBottom: 4 }}>Matches to price file SKU</div>
          {shortlist.length === 0 && <div style={{ fontSize: 13, color: "var(--color-text-3)" }}>No automatic suggestion — search the price file below to match it.</div>}
          {shortlist.map((s) => (
            <label key={s.sku} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer" }}>
              <input type="radio" name={`c-${c.key}`} checked={chosen === s.sku} onChange={() => setChosen(s.sku)} />
              <span style={{ fontFamily: "var(--font-mono, monospace)", fontWeight: 600, color: "var(--color-text-1)" }}>{s.sku}</span>
              {s.score != null && <span style={{ fontSize: 12, color: "var(--color-text-4)" }}>{Math.round(s.score * 100)}% match</span>}
            </label>
          ))}

          {/* Manual match: search the price file and pick any SKU */}
          <div style={{ marginTop: 10, borderTop: "1px solid var(--hairline)", paddingTop: 10 }}>
            <input
              className="form-input"
              placeholder="Search price file by code or description…"
              value={manualQ}
              onChange={(e) => setManualQ(e.target.value)}
              style={{ fontSize: 13, padding: "6px 10px" }}
            />
            {manualPicked && (
              <div style={{ fontSize: 12, color: "var(--color-text-3)", marginTop: 6 }}>
                Manually picked: <b style={{ fontFamily: "var(--font-mono, monospace)", color: "var(--color-text-1)" }}>{chosen}</b>
              </div>
            )}
            {searching && <div style={{ fontSize: 12, color: "var(--color-text-4)", marginTop: 6 }}>Searching…</div>}
            {manualResults.length > 0 && (
              <div style={{ maxHeight: 170, overflowY: "auto", border: "1px solid var(--hairline)", borderRadius: 8, marginTop: 6 }}>
                {manualResults.map((r) => (
                  <div
                    key={r.sku}
                    onClick={() => setChosen(r.sku)}
                    style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.05)", background: chosen === r.sku ? "var(--color-primary-16, rgba(236,72,153,0.12))" : "transparent" }}
                  >
                    <span style={{ fontFamily: "var(--font-mono, monospace)", fontWeight: 600, color: "var(--color-text-1)", fontSize: 13 }}>{r.sku}</span>
                    {r.description && <span style={{ fontSize: 12, color: "var(--color-text-3)", marginLeft: 8 }}>{r.description}</span>}
                  </div>
                ))}
              </div>
            )}
            {manualQ.trim().length >= 2 && !searching && manualResults.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--color-text-4)", marginTop: 6 }}>No price-file SKUs match that.</div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button
          className="btn btn-primary"
          disabled={busy || !chosen}
          onClick={() => onApprove(c.key, chosen)}
          style={{ opacity: chosen ? 1 : 0.5 }}
        >
          Approve → {chosen || "pick one"}
        </button>
        <button className="btn btn-secondary" disabled={busy} onClick={() => onReject(c.key)}>Reject</button>
      </div>
    </Card>
  );
}

export default function MatchReview() {
  usePageTitle("Match Review");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState("all");
  const [tier, setTier] = useState("all");
  const [suggestion, setSuggestion] = useState("all");   // all | with | without
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  const loadStats = useCallback(() => { apiGet("/match-review/stats").then(setStats).catch(() => {}); }, []);
  const loadQueue = useCallback(() => {
    setLoading(true);
    apiGet(`/match-review/queue?status=pending&supplier=${encodeURIComponent(supplier)}&tier=${tier}&suggestion=${suggestion}&limit=300`)
      .then((d) => { setItems(d?.items || []); setSuppliers(d?.suppliers || []); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [supplier, tier, suggestion]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const approve = async (key, file_sku) => {
    setBusy(true);
    try {
      await apiPost("/match-review/approve", { key, file_sku });
      setItems((prev) => prev.filter((c) => c.key !== key));
      loadStats();
      toast.success(`Approved → ${file_sku}. Saved as a reusable match.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const reject = async (key) => {
    setBusy(true);
    try {
      await apiPost("/match-review/reject", { key });
      setItems((prev) => prev.filter((c) => c.key !== key));
      loadStats();
      toast.success("Rejected. It won't be proposed again.");
    } catch (err) { toast.error(err.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  const rescan = async () => {
    setRescanning(true);
    toast.message ? toast.message("Rescanning invoices against the current price files…") : toast("Rescanning…");
    try {
      const res = await apiPost("/match-review/rebuild?confirm=true", {}, { timeout: 180000 });
      toast.success(`Rescan complete: ${Number(res?.queued_lines || 0).toLocaleString()} lines queued for review.`);
      loadStats();
      loadQueue();
    } catch (err) { toast.error(err.response?.data?.detail || "Rescan failed (it can take a while on a big invoice set)."); }
    finally { setRescanning(false); }
  };

  const kpis = [
    { label: "Pending", value: stats ? Number(stats.pending).toLocaleString() : "—" },
    { label: "Pending value", value: stats ? gbp(stats.pending_value) : "—" },
    { label: "Approved", value: stats ? Number(stats.approved).toLocaleString() : "—" },
    { label: "Rejected", value: stats ? Number(stats.rejected).toLocaleString() : "—" },
  ];

  return (
    <div>
      <PageHeader
        title="Match Review"
        subtitle="Approve or reject proposed matches between invoice codes and price-file SKUs. Approved matches are saved and applied automatically from then on."
      />
      <KpiRow items={kpis} cols={4} />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "14px 0 20px", alignItems: "center" }}>
        <select className="form-input" style={{ maxWidth: 240 }} value={supplier} onChange={(e) => setSupplier(e.target.value)}>
          <option value="all">All suppliers</option>
          {suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-input" style={{ maxWidth: 200 }} value={tier} onChange={(e) => setTier(e.target.value)}>
          <option value="all">All match types</option>
          <option value="t4">Code variant</option>
          <option value="t5">Description</option>
          <option value="manual">Manual (no auto match)</option>
        </select>
        <select className="form-input" style={{ maxWidth: 220 }} value={suggestion} onChange={(e) => setSuggestion(e.target.value)} data-testid="suggestion-filter">
          <option value="all">Suggested &amp; unsuggested</option>
          <option value="with">Has a suggested match</option>
          <option value="without">No suggestion (manual only)</option>
        </select>
        <button className="btn btn-secondary" onClick={() => { loadStats(); loadQueue(); }}>Refresh</button>
        <button className="btn btn-secondary" disabled={rescanning} onClick={rescan} title="Rescan invoices against the current price files to repopulate the queue">
          {rescanning ? "Rescanning…" : "Rescan queue"}
        </button>
      </div>

      {loading ? (
        <p style={{ opacity: 0.6 }}>Loading…</p>
      ) : items.length === 0 ? (
        <Card><p style={{ margin: 0, color: "var(--color-text-3)" }}>Nothing to review. Approved and rejected items drop off this list; run a rescan from the backend to repopulate after a recompute.</p></Card>
      ) : (
        items.map((c) => (
          <CandidateCard key={c.key} c={c} onApprove={approve} onReject={reject} busy={busy} />
        ))
      )}
    </div>
  );
}
